import { z } from 'zod'

// Zod 4 compila los objetos con `new Function`, que la CSP del portal bloquea.
z.config({ jitless: true })

/**
 * Contratos de publicaciones (P01 · C2 · C5), compartidos entre el frontend y
 * las funciones. Lo que valida el formulario es lo mismo que valida el
 * servidor, con los mensajes que ve la persona (§9).
 */

export const TIPOS = ['imagen', 'carrusel', 'video'] as const
export type TipoPublicacion = (typeof TIPOS)[number]

export const ESTADOS = ['borrador', 'para_revisar', 'cambios', 'aprobada'] as const
export type EstadoPublicacion = (typeof ESTADOS)[number]

export const REDES = ['instagram', 'facebook', 'tiktok', 'linkedin'] as const
export type RedSocial = (typeof REDES)[number]

export const ETIQUETA_RED: Record<RedSocial, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
}

export const ETIQUETA_TIPO: Record<TipoPublicacion, string> = {
  imagen: 'Imagen',
  carrusel: 'Carrusel',
  video: 'Video',
}

/**
 * DEC-21 — dónde sale la pieza. No es un tipo más: una historia puede ser
 * una imagen o un video.
 */
export const DESTINOS = ['feed', 'historia'] as const
export type DestinoPublicacion = (typeof DESTINOS)[number]

export const ETIQUETA_DESTINO: Record<DestinoPublicacion, string> = {
  feed: 'Feed',
  historia: 'Historia',
}

/**
 * Filtro de la grilla y del calendario (DEC-21, OBS-017): las dos vistas abren
 * con todo, para que el cliente siempre vea lo que tiene para revisar.
 */
export const FILTROS_DESTINO = ['todo', 'feed', 'historia'] as const
export type FiltroDestino = (typeof FILTROS_DESTINO)[number]

/**
 * DEC-20 — hora de publicación opcional, "HH:MM" de 24 horas en Buenos Aires.
 * Vacía es "sin hora".
 */
export const horaPautada = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .default(null)
  .refine((v) => v === null || /^([01]\d|2[0-3]):[0-5]\d$/.test(v), {
    message: 'La hora va como 18:30.',
  })

/**
 * Orden de las piezas de un mismo día: por hora, y las que no tienen hora al
 * final del día (DEC-20). Desempata por título para que no bailen.
 */
export function compararPorHora(
  a: { horaPautada: string | null; titulo: string },
  b: { horaPautada: string | null; titulo: string },
): number {
  if (a.horaPautada !== b.horaPautada) {
    if (a.horaPautada === null) return 1
    if (b.horaPautada === null) return -1
    return a.horaPautada < b.horaPautada ? -1 : 1
  }
  return a.titulo.localeCompare(b.titulo, 'es')
}

/** §8.5 — límites de cada tipo. El carrusel va de 2 a 20 láminas. */
export const LAMINAS = { min: 2, max: 20 } as const
export const TOPE_COPY = 2200
export const TOPE_TITULO = 60

/* ── Archivos de Drive ──────────────────────────────────── */

/**
 * Hasta la Fase 6 el archivo se pega como enlace de Drive o como identificador
 * (§1.4 del plan). El selector de Google llega con la carpeta del cliente.
 */
export function idDeArchivoDrive(valor: string): string | null {
  const v = valor.trim()
  if (!v) return null
  const enlace =
    /\/d\/([A-Za-z0-9_-]{10,})/.exec(v) ?? /[?&]id=([A-Za-z0-9_-]{10,})/.exec(v)
  if (enlace?.[1]) return enlace[1]
  if (/^[A-Za-z0-9_-]{10,}$/.test(v)) return v
  return null
}

export const enlaceDrive = (driveFileId: string) =>
  `https://drive.google.com/file/d/${driveFileId}/view`

/** Una línea por archivo; se aceptan saltos de línea, comas y espacios. */
export function leerArchivosPegados(texto: string): {
  ids: string[]
  invalidos: string[]
} {
  const partes = texto
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean)
  const ids: string[] = []
  const invalidos: string[] = []
  for (const parte of partes) {
    const id = idDeArchivoDrive(parte)
    if (id) {
      if (!ids.includes(id)) ids.push(id)
    } else {
      invalidos.push(parte)
    }
  }
  return { ids, invalidos }
}

/* ── Entradas ───────────────────────────────────────────── */

const listaDeArchivos = z.array(z.string().min(10)).default([])

/**
 * Lo que Drive informa de cada archivo elegido con el selector (Fase 6).
 * Opcional: los enlaces pegados a mano no lo traen.
 */
export const metadatoArchivo = z.object({
  mime: z.string().max(100),
  peso: z.number().int().nonnegative().nullable(),
  ancho: z.number().int().positive().nullable(),
  alto: z.number().int().positive().nullable(),
  duracionSeg: z.number().int().nonnegative().nullable(),
  blurhash: z.string().max(64).nullable(),
  miniatura: z.string().max(1000).nullable().default(null),
})
export type MetadatoArchivo = z.infer<typeof metadatoArchivo>
export const metadatosArchivos = z.record(z.string(), metadatoArchivo).default({})

export const publicacionEntrada = z
  .object({
    proyectoId: z.uuid(),
    titulo: z
      .string({ error: 'El título es obligatorio.' })
      .trim()
      .min(1, 'El título es obligatorio.')
      .max(TOPE_TITULO, `El título: hasta ${String(TOPE_TITULO)} caracteres.`),
    tipo: z.enum(TIPOS),
    destino: z.enum(DESTINOS).default('feed'),
    /** Identificadores de Drive, en orden. Portada aparte para el video. */
    archivos: listaDeArchivos,
    portada: z
      .string()
      .trim()
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .default(null),
    copy: z
      .string()
      .max(TOPE_COPY, `El copy: hasta ${String(TOPE_COPY)} caracteres.`)
      .default(''),
    /** "AAAA-MM-DD" en Buenos Aires. Sin restricción de rango (§8.5). */
    fechaPautada: z
      .string({ error: 'La fecha es obligatoria.' })
      .trim()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha es obligatoria.'),
    horaPautada,
    pilarIds: z.array(z.uuid()).min(1, 'Elegí al menos un pilar.'),
    redes: z.array(z.enum(REDES)).min(1, 'Elegí al menos una red.'),
    /** Por id de Drive: tipo real, medidas, duración y degradado. */
    metadatos: metadatosArchivos,
  })
  .superRefine((p, ctx) => {
    if (p.tipo === 'carrusel' && p.archivos.length > 0) {
      if (p.archivos.length < LAMINAS.min || p.archivos.length > LAMINAS.max) {
        ctx.addIssue({
          code: 'custom',
          path: ['archivos'],
          message: `Un carrusel lleva de ${String(LAMINAS.min)} a ${String(LAMINAS.max)} láminas.`,
        })
      }
    }
    if (p.tipo !== 'carrusel' && p.archivos.length > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['archivos'],
        message: 'Este tipo lleva un solo archivo.',
      })
    }
    if (p.tipo === 'video' && p.archivos.length > 0 && !p.portada) {
      ctx.addIssue({
        code: 'custom',
        path: ['portada'],
        message: 'El video necesita una portada.',
      })
    }
  })
export type PublicacionEntrada = z.input<typeof publicacionEntrada>

/** Al enviar a revisión sí o sí tiene que haber archivos (§8.5). */
export function faltanArchivos(tipo: TipoPublicacion, archivos: string[]): string | null {
  if (archivos.length === 0) return 'Elegí los archivos antes de enviar a revisión.'
  if (tipo === 'carrusel' && archivos.length < LAMINAS.min) {
    return `Un carrusel lleva de ${String(LAMINAS.min)} a ${String(LAMINAS.max)} láminas.`
  }
  return null
}

/** Edición de los datos que no versionan (§7.3). El tipo no se cambia. */
export const datosEntrada = z.object({
  titulo: z
    .string({ error: 'El título es obligatorio.' })
    .trim()
    .min(1, 'El título es obligatorio.')
    .max(TOPE_TITULO, `El título: hasta ${String(TOPE_TITULO)} caracteres.`),
  fechaPautada: z
    .string({ error: 'La fecha es obligatoria.' })
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha es obligatoria.'),
  horaPautada,
  /** Dónde sale no versiona: se cambia como la fecha (DEC-21). */
  destino: z.enum(DESTINOS).default('feed'),
  pilarIds: z.array(z.uuid()).min(1, 'Elegí al menos un pilar.'),
  redes: z.array(z.enum(REDES)).min(1, 'Elegí al menos una red.'),
})
export type DatosEntrada = z.input<typeof datosEntrada>

export const reprogramacionEntrada = z.object({
  fechaPautada: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'La fecha es obligatoria.'),
})

/* ── Respuestas ─────────────────────────────────────────── */

export interface ArchivoPublicacion {
  id: string
  rol: 'principal' | 'lamina' | 'portada'
  orden: number
  driveFileId: string
  /** Degradado guardado; hasta la Fase 4 no hay ninguno y se usa el de reserva. */
  blurhash: string | null
  /** Miniatura de Drive guardada al elegir el archivo (Fase 6). */
  miniatura: string | null
  mime: string
}

export interface PublicacionResumen {
  id: string
  titulo: string
  tipo: TipoPublicacion
  /** Feed o historia (DEC-21). */
  destino: DestinoPublicacion
  estado: EstadoPublicacion
  /** "AAAA-MM-DD" en Buenos Aires. */
  fechaPautada: string
  /** "HH:MM" en Buenos Aires, o null: sin hora (DEC-20). */
  horaPautada: string | null
  versionActual: number
  pilares: { id: string; nombre: string; color: number }[]
  /** Portada de la grilla: la lámina 1, o la portada del video. */
  portada: ArchivoPublicacion | null
  /** §7.4 — actividad de la otra parte desde la última visita. */
  nuevo: boolean
  dadaDeBaja: boolean
}

export interface PublicacionDetalle extends PublicacionResumen {
  proyectoId: string
  copy: string
  archivos: ArchivoPublicacion[]
  redes: RedSocial[]
}

export interface ContadorProyecto {
  proyectoId: string
  /** Cliente: piezas para revisar. RASTRO: piezas en cambios (§7.4). */
  paraRevisar: number
}

export interface GrillaRespuesta {
  publicaciones: PublicacionResumen[]
  /** Quedan más para "Mostrar más". */
  hayMas: number
  /** Meses con publicaciones, "AAAA-MM", del más nuevo al más viejo. */
  meses: string[]
  pilares: { id: string; nombre: string; color: number }[]
  /** Del proyecto, para el encabezado y la vista previa. */
  proyecto: {
    id: string
    nombre: string
    clienteNombre: string
    usuarioInstagram: string
    /** Sólo para RASTRO: la carpeta del cliente, donde se abre el selector de Drive. */
    driveFolderId: string | null
  }
  /** Piezas en borrador, para "Enviar N borradores a revisión". */
  borradores: number
  /** Piezas que el cliente tiene para revisar. */
  paraRevisar: number
  /**
   * La primera pieza para revisar, por fecha pautada, en todo el proyecto: la
   * grilla muestra un mes, y "Empezar revisión" no puede depender de cuál.
   */
  primeraParaRevisar: string | null
}
